import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { DevicePhoneMobileIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/auth';
import { authService } from '@/services';
import toast from 'react-hot-toast';
import type { BrokerOption, User } from '@/types';

type Step = 'phone' | 'otp' | 'broker_selection';

export function OTPLoginPage() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  // 9c: only the access token is held here; the refresh token is already in
  // an httpOnly cookie by the time verifyOtp resolves.
  const [pendingAccess, setPendingAccess] = useState<string | null>(null);

  const { isAuthenticated, login } = useAuthStore();
  const navigate = useNavigate();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setLoading(true);
    try {
      const response = await authService.requestOtp(phone.trim(), 'login');
      toast.success(response.message);
      setStep('otp');
    } catch (error: unknown) {
      // A 503 (delivery unavailable) carries `message`; validation errors
      // carry `error`. Reading only `error` turned "we cannot text you yet,
      // use your email" into a bare "Failed to send OTP".
      const err = error as { response?: { data?: { error?: string; message?: string } } };
      toast.error(
        err?.response?.data?.message || err?.response?.data?.error || 'Failed to send OTP',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;

    setLoading(true);
    try {
      const data = await authService.verifyOtp(phone.trim(), otp.trim(), 'login');

      if (data.requires_broker_selection) {
        // Multi-broker user - show selection
        setBrokers(data.brokers);
        setPendingUser(data.user);
        setPendingAccess(data.access);
        setStep('broker_selection');
      } else if (data.brokers.length === 1) {
        // Single broker - auto-select
        const broker = data.brokers[0];
        login(data.user, data.access, 'broker', {
          id: broker.id,
          name: broker.name,
          code: broker.code,
          logo_url: broker.logo_url,
          tier: '',
          custom_domain: '',
          primary_color: '',
          secondary_color: '',
        });
        toast.success(`Welcome to ${broker.name}`);
        navigate('/dashboard');
      } else {
        // No broker association - login normally
        login(data.user, data.access);
        toast.success('Login successful');
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBroker = async (broker: BrokerOption) => {
    if (!pendingUser || !pendingAccess) return;

    setLoading(true);
    try {
      await authService.selectBroker(broker.id);
      login(pendingUser, pendingAccess, 'broker', {
        id: broker.id,
        name: broker.name,
        code: broker.code,
        logo_url: broker.logo_url,
        tier: '',
        custom_domain: '',
        primary_color: '',
        secondary_color: '',
      });
      toast.success(`Welcome to ${broker.name}`);
      navigate('/dashboard');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || 'Failed to select broker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4">
              <DevicePhoneMobileIcon className="h-8 w-8" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">OTP Login</h1>
            <p className="text-gray-500 mt-1">Sign in with a one-time code</p>
          </div>

          {step === 'phone' && (
            <form onSubmit={handleRequestOTP} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Email or mobile number
                </label>
                <input
                  type="text"
                  id="phone"
                  inputMode="email"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="input"
                  placeholder="you@example.com or +91 XXXXXXXXXX"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || !phone.trim()}
                className="w-full btn bg-blue-600 hover:bg-blue-700 text-white py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending OTP...' : 'Get OTP'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <p className="text-sm text-gray-600 text-center">
                Enter the code sent to <strong>{phone}</strong>
              </p>
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                  OTP Code
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full btn bg-blue-600 hover:bg-blue-700 text-white py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                className="w-full text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Change Number
              </button>
            </form>
          )}

          {step === 'broker_selection' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                You have access to multiple brokers. Select one:
              </p>
              <div className="space-y-3">
                {brokers.map((broker) => (
                  <button
                    key={broker.id}
                    onClick={() => handleSelectBroker(broker)}
                    disabled={loading}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                  >
                    <div className="flex-shrink-0">
                      <BuildingOffice2Icon className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{broker.name}</p>
                      <p className="text-xs text-gray-500">Role: {broker.role}</p>
                    </div>
                    {broker.is_primary && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Primary
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200">
            <Link
              to="/login-select"
              className="block text-center text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              &larr; Back to login options
            </Link>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500">
          &copy; {new Date().getFullYear()} base.Policy.online. All rights reserved.
        </p>
      </div>
    </div>
  );
}
